/*
 * AFTER EDITING THIS FILE: run `npm run deploy` in Desktop\ClaudiusCodius\GBG3.0\gbg-api. The price check / cart deals worker runs its
 * own copy of this file; until it is redeployed it refuses checkouts whose price changed (dashboard shows a red banner).
 *
 * GBG build compatibility rules - the ONE place that decides whether parts fit a case.
 *
 * Pure functions, no DOM and no page state, so the product-page configurator, the
 * build finder, and any future page/worker use identical rules. Inputs are plain data
 * from gbg-cases.json (case records + rules) and gbg-part-descriptions.json (parts).
 *
 * Loaded as window.GBGCompat (browser) or via require() (node tests).
 *
 * Conventions:
 *  - Missing data is reported as `known: false`; `fits` stays true for it so existing
 *    behavior is unchanged, but callers that must be strict can check `known`.
 *  - `cooler` args are catalog cooler parts (or null when the cooler is not known).
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.GBGCompat = api;
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  // Front radiator GPU-clearance defaults (gbg-cases.json rules can override): radiator thickness,
  // fan thickness (only counted when the case has no room for fans in the front panel), safety margin.
  var FRONT = { radiator: 27, fans: 25, margin: 5 };

  function num(v) {
    if (v == null || v === '') return null;
    var n = Number(v);
    return isFinite(n) ? n : null;
  }

  function norm(t) {
    return String(t == null ? '' : t).toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  }

  /* ---------------- Cases ---------------- */

  // Finds the case record for a product title / handle / id. Exact id wins, otherwise the
  // longest matching token (case name, id, or "match" alias) found in the query.
  function findCase(caseBook, query) {
    var list = (caseBook && caseBook.cases) || [];
    var queries = (Array.isArray(query) ? query : [query]).map(norm).filter(Boolean);
    if (!queries.length || !list.length) return null;
    var i, q;
    for (i = 0; i < list.length; i++) {
      for (q = 0; q < queries.length; q++) {
        if (norm(list[i].id) === queries[q]) return list[i];
      }
    }
    var hay = ' ' + queries.join(' | ') + ' ';
    var best = null;
    var bestLen = 0;
    list.forEach(function (rec) {
      (rec.match || []).concat([rec.name, rec.id]).forEach(function (token) {
        var t = norm(token);
        if (t && hay.indexOf(t) !== -1 && t.length > bestLen) {
          best = rec;
          bestLen = t.length;
        }
      });
    });
    return best;
  }

  /* ---------------- Coolers ---------------- */

  function coolerAioMm(part) {
    if (!part || part.kind === 'air') return 0;
    var explicit = num(part.aio_mm);
    if (explicit != null) return explicit || 0;
    var blob = String(part.id || '') + ' ' + String(part.label || '');
    var m = blob.match(/(\d+)\s*mm/i);
    return m ? Number(m[1]) : 0;
  }

  /* ---------------- Radiator mounts ---------------- */
  // caseRec.radiators = { front: mm, top: mm, side: mm }: the biggest radiator each spot takes.
  // Only a FRONT radiator sits in the GPU's path (see frontRadiatorMm), so a
  // radiator always goes top/side when one of those spots is big enough, and front only when
  // nothing else fits. Old records without `radiators` fall back to largest_aio_mm @ largest_aio_mount.
  var MOUNT_ORDER = ['top', 'side', 'front'];

  function radiatorMounts(caseRec) {
    if (!caseRec) return [];
    var r = caseRec.radiators;
    if (r && typeof r === 'object') {
      return MOUNT_ORDER.filter(function (pos) { return num(r[pos]) > 0; })
        .map(function (pos) { return { pos: pos, max: num(r[pos]) }; });
    }
    var max = num(caseRec.largest_aio_mm);
    return max ? [{ pos: caseRec.largest_aio_mount || 'front', max: max }] : [];
  }

  // Where this cooler's radiator goes: { pos, max }, null for air coolers / unknown case,
  // false when no spot in the case takes a radiator this big.
  function radiatorMount(cooler, caseRec) {
    var mm = coolerAioMm(cooler);
    if (!mm || !caseRec) return null;
    var mounts = radiatorMounts(caseRec);
    if (!mounts.length) return caseRec.radiators ? false : null;
    for (var i = 0; i < mounts.length; i++) if (mm <= mounts[i].max) return mounts[i];
    return false;
  }

  function coolerFits(cooler, caseRec) {
    return radiatorMount(cooler, caseRec) !== false;
  }

  /* ---------------- GPUs ---------------- */

  function gpuLengthMm(gpu) {
    if (!gpu) return 0;
    return num(gpu.length_mm) || 0;
  }

  // Usable GPU length in this case with this cooler: a radiator that has to go in the FRONT
  // takes frontRadiatorMm() of clearance; top/side radiators and air coolers take none.
  // GPU length a front radiator takes: radiator + margin, plus the fans when they must sit inside the
  // case. caseRec.front_fans_in_panel = true when the fans fit in the front panel (outside the GPU area).
  function frontRadiatorMm(caseRec, rules) {
    var r = rules || {};
    var radiator = num(r.front_radiator_mm); if (radiator == null) radiator = FRONT.radiator;
    var fans = num(r.front_fan_mm); if (fans == null) fans = FRONT.fans;
    var margin = num(r.front_margin_mm); if (margin == null) margin = FRONT.margin;
    return radiator + margin + (caseRec && caseRec.front_fans_in_panel ? 0 : fans);
  }

  function usableVgaMm(caseRec, rules, cooler) {
    if (!caseRec) return null;
    var vga = num(caseRec.vga_length_mm);
    if (vga == null) return null;
    var mount = radiatorMount(cooler, caseRec);
    if (mount && mount.pos === 'front') {
      vga -= frontRadiatorMm(caseRec, rules);
    }
    return vga;
  }

  function gpuFitsLength(gpu, maxMm) {
    if (!gpu) return true;
    if (maxMm == null || !isFinite(maxMm)) return true;
    var len = gpuLengthMm(gpu);
    if (!len) return true;
    return len <= maxMm;
  }

  // -> { fits, known, len, max }. known=false when the GPU length or case clearance is missing.
  function gpuFits(gpu, caseRec, rules, cooler) {
    var max = usableVgaMm(caseRec, rules, cooler);
    var len = gpuLengthMm(gpu);
    if (!gpu || max == null || !len) return { fits: true, known: false, len: len, max: max };
    return { fits: len <= max, known: true, len: len, max: max };
  }

  // The cooler this GPU should get. Keeps `cooler` when the pair fits. When the radiator is what
  // blocks the GPU (front mount eats clearance), it steps down to the BIGGEST radiator that still
  // works (a free top/side spot), and among those the CHEAPEST; with no such spot, the cheapest air
  // cooler. Returns null only when the GPU does not fit even with an air cooler (then the GPU itself
  // is too long for the case).
  function coolerForGpu(coolers, cooler, gpu, caseRec, rules) {
    var fits = function (c) { return coolerFits(c, caseRec) && gpuFits(gpu, caseRec, rules, c).fits; };
    if (cooler && fits(cooler)) return cooler;
    var ok = (coolers || []).filter(fits);
    if (!ok.length) return null;
    var wantWater = coolerAioMm(cooler) > 0;
    var water = ok.filter(function (c) { return coolerAioMm(c) > 0; });
    var pool = wantWater && water.length ? water : ok.filter(function (c) { return !coolerAioMm(c); });
    if (!pool.length) pool = ok;
    pool.sort(function (a, b) {
      return (coolerAioMm(b) - coolerAioMm(a)) || (Number(a.price || 0) - Number(b.price || 0));
    });
    return pool[0];
  }

  // "Front mounted 360MM" / "Top mounted 240MM" / "ARGB Air" - for messages to the shopper.
  function coolerPlacement(cooler, caseRec) {
    var mount = radiatorMount(cooler, caseRec);
    if (mount) return mount.pos.charAt(0).toUpperCase() + mount.pos.slice(1) + ' mounted ' + coolerAioMm(cooler) + 'MM';
    return (cooler && (cooler.short || cooler.label)) || 'air cooler';
  }

  // Largest fitting GPU from `gpus` (catalog parts) - "largest" by the order given by
  // `rank` (default: length). Returns null if none fit.
  function largestFittingGpu(gpus, caseRec, rules, cooler, rank) {
    var score = rank || gpuLengthMm;
    var best = null;
    (gpus || []).forEach(function (g) {
      if (!gpuFits(g, caseRec, rules, cooler).fits) return;
      if (!best || score(g) > score(best)) best = g;
    });
    return best;
  }

  /* ---------------- Power ---------------- */

  // PSU tier a part needs (GPU) or provides (PSU): 0 = base 550W, 1 = 850W, 2 = 1000W+.
  // Uses the explicit psu_step when the catalog has one; otherwise a GPU flagged
  // wattage:'high' needs at least the 850W step. (Radeon cards had no psu_step, so the
  // configurator treated them as needing no more than the base 550W.)
  function psuStep(part) {
    if (!part) return 0;
    var n = num(part.psu_step);
    if (n != null) return n;
    return part.wattage === 'high' ? 1 : 0;
  }

  /* ---------------- Whole-build check ---------------- */

  // opts: { caseRec, rules, gpu, cooler, psu }. Returns { ok, issues: [{ code, message, ... }] }.
  // Add new rules (PSU, motherboard size, RAM height...) here so every caller gets them.
  function checkBuild(opts) {
    var o = opts || {};
    var issues = [];
    var caseName = (o.caseRec && (o.caseRec.name || o.caseRec.id)) || 'this case';

    var g = gpuFits(o.gpu, o.caseRec, o.rules, o.cooler);
    if (g.known && !g.fits) {
      var mount = radiatorMount(o.cooler, o.caseRec);
      var frontCooler = !!(mount && mount.pos === 'front');
      // Radiator-caused? Then the fix is a smaller/relocated radiator, never a smaller GPU.
      var radiatorCaused = frontCooler && gpuFits(o.gpu, o.caseRec, o.rules, null).fits;
      var fixCooler = radiatorCaused && o.coolers ? coolerForGpu(o.coolers, o.cooler, o.gpu, o.caseRec, o.rules) : null;
      issues.push({
        code: radiatorCaused ? 'radiator-blocks-gpu' : 'gpu-too-long',
        len: g.len,
        max: g.max,
        fix: fixCooler ? { slot: 'cooler', part: fixCooler } : null,
        message: 'The ' + ((o.gpu && (o.gpu.short || o.gpu.label)) || 'GPU') + ' (' + g.len + ' mm) will not fit the ' +
          caseName + ' (max ' + g.max + ' mm' + (frontCooler ? ' with a front radiator' : '') + ').' +
          (fixCooler ? ' Use the ' + (fixCooler.short || fixCooler.label) + ' instead.' : '')
      });
    }

    if (o.psu && o.gpu && psuStep(o.gpu) > psuStep(o.psu)) {
      issues.push({
        code: 'psu-too-small',
        message: 'The ' + (o.gpu.short || o.gpu.label || 'GPU') + ' needs a larger power supply than ' + (o.psu.label || 'the selected PSU') + '.'
      });
    }

    if (o.cooler && !coolerFits(o.cooler, o.caseRec)) {
      issues.push({
        code: 'cooler-too-large',
        message: 'That cooler will not fit the ' + caseName + ' (' + radiatorMounts(o.caseRec).map(function (m) { return m.max + ' mm ' + m.pos; }).join(', ') + ').'
      });
    }

    return { ok: issues.length === 0, issues: issues };
  }

  return {
    findCase: findCase,
    coolerAioMm: coolerAioMm,
    coolerFits: coolerFits,
    radiatorMounts: radiatorMounts,
    radiatorMount: radiatorMount,
    coolerForGpu: coolerForGpu,
    coolerPlacement: coolerPlacement,
    gpuLengthMm: gpuLengthMm,
    usableVgaMm: usableVgaMm,
    gpuFitsLength: gpuFitsLength,
    gpuFits: gpuFits,
    largestFittingGpu: largestFittingGpu,
    psuStep: psuStep,
    checkBuild: checkBuild,
    frontRadiatorMm: frontRadiatorMm
  };
});
